import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { toast } from 'sonner';
import api, { buildWhatsAppLink } from '../lib/api';
import SearchableLocation from './SearchableLocation';
import QuoteResults from './QuoteResults';
import { Loader2, Send } from 'lucide-react';

const ReservationForm = () => {
  const { t, lang } = useLanguage();
  const [type, setType] = useState('transfer');
  const [tripType, setTripType] = useState('one-way');
  const [airports, setAirports] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [excursions, setExcursions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [selectedVehicleKey, setSelectedVehicleKey] = useState(null);
  const [estimatedPrice, setEstimatedPrice] = useState(null);
  const [form, setForm] = useState({
    full_name: '', email: '', whatsapp: '',
    pickup: '', destination: '',
    excursion_id: '', excursion_title: '',
    date: '', time: '', passengers: '2',
    vehicle: '', comments: '',
  });

  useEffect(() => {
    api.get('/airports').then(r => setAirports(r.data)).catch(()=>{});
    api.get('/hotels').then(r => setHotels(r.data)).catch(()=>{});
    api.get('/vehicles').then(r => setVehicles(r.data)).catch(()=>{});
    api.get('/excursions').then(r => setExcursions(r.data)).catch(()=>{});
  }, []);

  useEffect(() => {
    const onPrefill = (e) => {
      const d = e.detail || {};
      setForm(f => ({ ...f, pickup: d.pickup || f.pickup, destination: d.destination || f.destination, date: d.date || f.date, passengers: d.pax || f.passengers }));
    };
    const onExc = (e) => {
      const exc = e.detail;
      setType('excursion');
      setForm(f => ({ ...f, excursion_id: exc.id, excursion_title: exc.title }));
    };
    const onVeh = (e) => {
      const v = e.detail;
      setForm(f => ({ ...f, vehicle: v.name }));
    };
    window.addEventListener('prefillReservation', onPrefill);
    window.addEventListener('selectExcursion', onExc);
    window.addEventListener('selectVehicle', onVeh);
    return () => {
      window.removeEventListener('prefillReservation', onPrefill);
      window.removeEventListener('selectExcursion', onExc);
      window.removeEventListener('selectVehicle', onVeh);
    };
  }, []);

  // Fetch quote when transfer details change
  useEffect(() => {
    if (type !== 'transfer' || !form.pickup || !form.destination) {
      setQuote(null);
      setEstimatedPrice(null);
      return;
    }
    setQuoteLoading(true);
    const timer = setTimeout(() => {
      api.post('/quote', {
        pickup: form.pickup,
        destination: form.destination,
        passengers: parseInt(form.passengers || '2', 10),
        trip_type: tripType,
      }).then(r => {
        setQuote(r.data);
        setQuoteLoading(false);
        if (r.data.ok && selectedVehicleKey) {
          const sel = r.data.quotes.find(q => q.vehicle_key === selectedVehicleKey);
          if (sel) setEstimatedPrice(sel.price_total);
        }
      }).catch(() => { setQuoteLoading(false); });
    }, 600);
    return () => clearTimeout(timer);
  }, [type, form.pickup, form.destination, form.passengers, tripType, selectedVehicleKey]);

  const handleSelectVehicleQuote = (q) => {
    setSelectedVehicleKey(q.vehicle_key);
    setEstimatedPrice(q.price_total);
    // Map vehicle key to vehicle name for the form field
    const v = vehicles.find(x => x.pricing_key === q.vehicle_key);
    if (v) update('vehicle', v.name);
  };

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.whatsapp || !form.date) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        type,
        full_name: form.full_name,
        email: form.email,
        whatsapp: form.whatsapp,
        trip_type: tripType,
        pickup: form.pickup,
        destination: form.destination,
        excursion_id: form.excursion_id,
        excursion_title: form.excursion_title,
        date: form.date,
        time: form.time,
        passengers: parseInt(form.passengers || '2', 10),
        vehicle: form.vehicle,
        comments: estimatedPrice ? `${form.comments} | Precio estimado: $${estimatedPrice} USD` : form.comments,
      };
      await api.post('/reservations', payload);
      toast.success(t.reservation.success);
      const priceMsg = estimatedPrice ? ` Precio estimado: $${estimatedPrice} USD.` : '';
      // Open WhatsApp
      const msg = type === 'excursion'
        ? `Hola TTS Santos, quiero reservar la excursión: ${form.excursion_title}. Nombre: ${form.full_name}, Fecha: ${form.date}, Pasajeros: ${form.passengers}.`
        : `Hola TTS Santos, quiero reservar un traslado de ${form.pickup} a ${form.destination}. Nombre: ${form.full_name}, Fecha: ${form.date} ${form.time}, Pasajeros: ${form.passengers}, Vehículo: ${form.vehicle || 'a determinar'}.${priceMsg}`;
      window.open(buildWhatsAppLink(msg), '_blank');
      setForm({ full_name: '', email: '', whatsapp: '', pickup: '', destination: '', excursion_id: '', excursion_title: '', date: '', time: '', passengers: '2', vehicle: '', comments: '' });
    } catch (err) {
      toast.error(t.reservation.error);
    } finally {
      setLoading(false);
    }
  };

  const paxOptions = [1,2,3,4,5,6,7,8,9,10,11,12,15,20,25,30];

  return (
    <section id="reservation" className="py-24 bg-[#0a0a0a] relative">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-[#ff6b1a] text-sm font-bold tracking-[0.2em] uppercase">{t.reservation.tag}</span>
          <h2 className="text-4xl md:text-5xl font-black text-white mt-3 mb-4">{t.reservation.title}</h2>
          <p className="text-white/60 text-lg">{t.reservation.subtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-6 md:p-10 space-y-6">
          {/* Type tabs */}
          <div className="flex gap-2 p-1 bg-black/40 rounded-lg w-fit mx-auto">
            <button type="button" onClick={() => setType('transfer')} className={`px-6 py-2 rounded-md text-sm font-semibold transition-colors ${type === 'transfer' ? 'bg-[#ff6b1a] text-white' : 'text-white/60'}`}>{t.reservation.transfer}</button>
            <button type="button" onClick={() => setType('excursion')} className={`px-6 py-2 rounded-md text-sm font-semibold transition-colors ${type === 'excursion' ? 'bg-[#ff6b1a] text-white' : 'text-white/60'}`}>{t.reservation.excursion}</button>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            <div>
              <Label className="text-white/80 mb-2 block">{t.reservation.fullName} *</Label>
              <Input value={form.full_name} onChange={(e) => update('full_name', e.target.value)} required className="bg-black/40 border-white/10 text-white h-11" />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">{t.reservation.email} *</Label>
              <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required className="bg-black/40 border-white/10 text-white h-11" />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">{t.reservation.whatsapp} *</Label>
              <Input value={form.whatsapp} onChange={(e) => update('whatsapp', e.target.value)} required placeholder="+1 829..." className="bg-black/40 border-white/10 text-white h-11" />
            </div>
          </div>

          {type === 'transfer' ? (
            <>
              <div>
                <Label className="text-white/80 mb-3 block">{t.reservation.tripType}</Label>
                <RadioGroup value={tripType} onValueChange={setTripType} className="flex gap-6">
                  <div className="flex items-center gap-2"><RadioGroupItem value="one-way" id="ow" className="border-white/40 text-[#ff6b1a]" /><Label htmlFor="ow" className="text-white/80">{t.reservation.oneWay}</Label></div>
                  <div className="flex items-center gap-2"><RadioGroupItem value="round-trip" id="rt" className="border-white/40 text-[#ff6b1a]" /><Label htmlFor="rt" className="text-white/80">{t.reservation.roundTrip}</Label></div>
                </RadioGroup>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-white/80 mb-2 block">{t.reservation.pickup} *</Label>
                  <SearchableLocation value={form.pickup} onChange={(v) => update('pickup', v)} airports={airports} hotels={hotels} placeholder={t.hero.pickupPh} />
                </div>
                <div>
                  <Label className="text-white/80 mb-2 block">{t.reservation.destination} *</Label>
                  <SearchableLocation value={form.destination} onChange={(v) => update('destination', v)} airports={airports} hotels={hotels} placeholder={t.hero.destinationPh} />
                </div>
              </div>
            </>
          ) : (
            <div>
              <Label className="text-white/80 mb-2 block">{t.reservation.excursionLabel} *</Label>
              <Select value={form.excursion_id} onValueChange={(v) => {
                const exc = excursions.find(e => e.id === v);
                update('excursion_id', v);
                update('excursion_title', exc ? exc.title : '');
              }}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white h-11"><SelectValue placeholder={t.reservation.excursionPh} /></SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-white/10 max-h-72">
                  {excursions.map(e => <SelectItem key={e.id} value={e.id} className="text-white">{lang === 'en' && e.title_en ? e.title_en : e.title} - ${e.price.toFixed(0)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-5">
            <div>
              <Label className="text-white/80 mb-2 block">{t.reservation.date} *</Label>
              <Input type="date" value={form.date} onChange={(e) => update('date', e.target.value)} required className="bg-black/40 border-white/10 text-white h-11" />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">{t.reservation.time}</Label>
              <Input type="time" value={form.time} onChange={(e) => update('time', e.target.value)} className="bg-black/40 border-white/10 text-white h-11" />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">{t.reservation.passengers} *</Label>
              <Select value={form.passengers} onValueChange={(v) => update('passengers', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white h-11"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-white/10">
                  {paxOptions.map(p => <SelectItem key={p} value={String(p)} className="text-white">{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {type === 'transfer' && (
            <div>
              <Label className="text-white/80 mb-2 block">{t.reservation.vehicle}</Label>
              <Select value={form.vehicle} onValueChange={(v) => update('vehicle', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white h-11"><SelectValue placeholder={t.reservation.vehiclePh} /></SelectTrigger>
                <SelectContent className="bg-[#0f0f0f] border-white/10 max-h-72">
                  {vehicles.map(v => <SelectItem key={v.id} value={v.name} className="text-white">{lang === 'en' && v.name_en ? v.name_en : v.name} ({v.capacity})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {type === 'transfer' && (quote || quoteLoading) && (
            <QuoteResults
              quote={quote}
              loading={quoteLoading}
              onSelectVehicle={handleSelectVehicleQuote}
              selectedKey={selectedVehicleKey}
            />
          )}

          <div>
            <Label className="text-white/80 mb-2 block">{t.reservation.comments}</Label>
            <Textarea value={form.comments} onChange={(e) => update('comments', e.target.value)} rows={3} className="bg-black/40 border-white/10 text-white" />
          </div>

          <Button type="submit" disabled={loading} className="btn-orange border-0 w-full h-12 rounded-md text-base font-bold gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-5 h-5" /> {t.reservation.submit}</>}
          </Button>
        </form>
      </div>
    </section>
  );
};

export default ReservationForm;
